"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Stack,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
} from "@mui/material"
import {
  Edit as EditIcon,
  Add as AddIcon,
  Grade as GradeIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Percent as PercentIcon,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import axios from "axios"
import { baseURL, config, requestID } from "../../../common/utils"
import { IonContent, IonPage } from "@ionic/react"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 12,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

const GradeChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.875rem",
  height: 32,
  borderRadius: 8,
}))

interface Grade {
  grade_id: string
  grade_letter: string
  grade_value: number
}

const GradeSetupPage: React.FC = () => {
  const [grades, setGrades] = useState<Grade[]>([])
  const [open, setOpen] = useState(false)
  const [gradeName, setGradeName] = useState("")
  const [gradeValue, setGradeValue] = useState("")
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  })

  useEffect(() => {
    fetchGrades()
  }, [])

  const fetchGrades = async () => {
    setLoading(true)
    try {
      const payload = { requestID: requestID() }
      const res = await axios.post(`${baseURL}warehouses/getAll_customer_product_grade`, payload, config)
      setGrades(res.data.data || [])
    } catch {
      setToast({ open: true, message: "Error fetching grades", severity: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleOpen = (grade: Grade | null = null) => {
    if (grade) {
      setSelectedGrade(grade)
      setGradeName(grade.grade_letter)
      setGradeValue(grade.grade_value.toString())
    } else {
      setSelectedGrade(null)
      setGradeName("")
      setGradeValue("")
    }
    setOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const payload = {
      requestID: requestID(),
      grade_letter: gradeName.trim().toUpperCase(),
      grade_value: Number.parseFloat(gradeValue),
    }

    const url = selectedGrade
      ? `${baseURL}warehouses/update_customer_product_grade`
      : `${baseURL}warehouses/create_customer_product_grade`

    if (selectedGrade) {
      payload.grade_id = selectedGrade.grade_id
    }

    try {
      setSubmitLoading(true)
      const res = await axios.post(url, payload, config)

      if (res.data.status === "true") {
        setToast({
          open: true,
          message: selectedGrade ? "Grade updated successfully!" : "Grade created successfully!",
          severity: "success",
        })
        handleClose()
        fetchGrades()
      } else {
        throw new Error(res.data.message || "Operation failed")
      }
    } catch (err: any) {
      setToast({ open: true, message: err.message, severity: "error" })
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleClose = () => {
    setOpen(false)
    setGradeName("")
    setGradeValue("")
    setSelectedGrade(null)
  }

  const getGradeColor = (value: number) => {
    if (value >= 0.8) return "#10b981" // Green for high grades
    if (value >= 0.6) return "#f59e0b" // Yellow for medium grades
    if (value >= 0.4) return "#ef4444" // Red for low grades
    return "#6b7280" // Gray for very low grades
  }

  const getGradeColorName = (value: number) => {
    if (value >= 0.8) return "success"
    if (value >= 0.6) return "warning"
    if (value >= 0.4) return "error"
    return "default"
  }

  return (
    <IonPage>
      <IonContent>
        <Container maxWidth="lg" sx={{ py: 3 }}>
          {/* Header */}
          <StyledCard sx={{ mb: 3 }}>
            <HeaderCard>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    color="#1e293b"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <GradeIcon />
                    Grade Management
                  </Typography>
                  <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                    Configure product quality grades and their values
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchGrades}
                    disabled={loading}
                    sx={{ color: "#64748b", borderColor: "#d1d5db" }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpen()}
                    sx={{
                      bgcolor: "#1e293b",
                      "&:hover": { bgcolor: "#334155" },
                      textTransform: "none",
                      px: 3,
                    }}
                  >
                    Add Grade
                  </Button>
                </Stack>
              </Box>
            </HeaderCard>
          </StyledCard>

          {/* Stats */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <StatsCard>
                <GradeIcon sx={{ fontSize: 32, mb: 1, color: "#64748b" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {grades.length}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Total Grades
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatsCard>
                <PercentIcon sx={{ fontSize: 32, mb: 1, color: "#10b981" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {grades.length > 0 ? `${(Math.max(...grades.map((g) => g.grade_value)) * 100).toFixed(0)}%` : "0%"}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Highest Grade
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatsCard>
                <PercentIcon sx={{ fontSize: 32, mb: 1, color: "#ef4444" }} />
                <Typography variant="h4" fontWeight="600" color="#1e293b">
                  {grades.length > 0 ? `${(Math.min(...grades.map((g) => g.grade_value)) * 100).toFixed(0)}%` : "0%"}
                </Typography>
                <Typography variant="body2" color="#64748b">
                  Lowest Grade
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Main Content */}
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              {loading ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="#64748b" sx={{ mt: 2 }}>
                    Loading grades...
                  </Typography>
                </Box>
              ) : grades.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <GradeIcon sx={{ fontSize: 64, color: "#cbd5e1", mb: 2 }} />
                  <Typography variant="h6" color="#64748b" sx={{ mb: 1 }}>
                    No Grades Available
                  </Typography>
                  <Typography variant="body2" color="#94a3b8" sx={{ mb: 3 }}>
                    Create your first grade to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpen()}
                    sx={{
                      bgcolor: "#1e293b",
                      "&:hover": { bgcolor: "#334155" },
                      textTransform: "none",
                    }}
                  >
                    Add First Grade
                  </Button>
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8fafc" }}>
                        <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Grade</TableCell>
                        <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Value</TableCell>
                        <TableCell sx={{ fontWeight: "600", color: "#374151" }}>Percentage</TableCell>
                        <TableCell sx={{ fontWeight: "600", color: "#374151" }} align="center">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grades
                        .sort((a, b) => b.grade_value - a.grade_value)
                        .map((grade) => (
                          <TableRow key={grade.grade_id} hover>
                            <TableCell>
                              <GradeChip
                                label={grade.grade_letter}
                                sx={{
                                  bgcolor: getGradeColor(grade.grade_value),
                                  color: "white",
                                  minWidth: 48,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" fontWeight="500" color="#1e293b">
                                {grade.grade_value}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 100,
                                    height: 8,
                                    bgcolor: "#f1f5f9",
                                    borderRadius: 4,
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${grade.grade_value * 100}%`,
                                      height: "100%",
                                      bgcolor: getGradeColor(grade.grade_value),
                                      borderRadius: 4,
                                    }}
                                  />
                                </Box>
                                <Typography variant="body2" fontWeight="600" color="#1e293b">
                                  {(grade.grade_value * 100).toFixed(0)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                onClick={() => handleOpen(grade)}
                                sx={{ color: "#64748b", "&:hover": { bgcolor: "#f1f5f9" } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </StyledCard>

          {/* Dialog */}
          <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" fontWeight="600">
                  {selectedGrade ? "Edit Grade" : "Add New Grade"}
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: "#64748b" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Grade Letter"
                  value={gradeName}
                  onChange={(e) => setGradeName(e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 1 }}
                  required
                  placeholder="e.g., A"
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <GradeIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Grade Value"
                  type="number"
                  value={gradeValue}
                  onChange={(e) => setGradeValue(e.target.value)}
                  required
                  placeholder="e.g., 0.85 for 85%"
                  inputProps={{ min: 0, max: 1, step: 0.01 }}
                  helperText="Enter a decimal value between 0 and 1 (e.g., 0.85 for 85%)"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                {gradeValue && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                      Preview:
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <GradeChip
                        label={gradeName || "?"}
                        sx={{
                          bgcolor: getGradeColor(Number.parseFloat(gradeValue) || 0),
                          color: "white",
                        }}
                      />
                      <Typography variant="body1" fontWeight="500">
                        {(Number.parseFloat(gradeValue) * 100 || 0).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 3 }}>
              <Button onClick={handleClose} sx={{ color: "#64748b", textTransform: "none" }}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={submitLoading || !gradeName.trim() || !gradeValue}
                startIcon={submitLoading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  bgcolor: "#1e293b",
                  "&:hover": { bgcolor: "#334155" },
                  textTransform: "none",
                  px: 3,
                }}
              >
                {submitLoading ? "Saving..." : selectedGrade ? "Update Grade" : "Create Grade"}
              </Button>
            </DialogActions>
          </StyledDialog>

          {/* Toast */}
          <Snackbar
            open={toast.open}
            autoHideDuration={4000}
            onClose={() => setToast({ ...toast, open: false })}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={() => setToast({ ...toast, open: false })}
              severity={toast.severity}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default GradeSetupPage
